import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Input,
  useToast,
} from "@chakra-ui/react";
import {
  MAX_NAME_LENGTH,
  MAX_PASSWORD_LENGTH,
  MIN_NAME_LENGTH,
  MIN_PASSWORD_LENGTH,
} from "common";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { LOGGED_IN_HOME_PAGE } from "../config";
import { Logo } from "../utils/Logo";
import { THEME_COLOR_SCHEME } from "../utils/theme";

export const RegisterPage = () => {
  const [fName, setFName] = useState("");
  const [lName, setLName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [search] = useSearchParams();
  const appCode = useMemo(() => search.get("code") ?? undefined, [search]);
  const api = useAPI();
  const toast = useToast();
  const { data, state, error, query } = useAPIQuery<"authRegister">({
    apiCall: api.authRegister,
  });
  const navigate = useNavigate();
  useToastOnAPIError({ title: "Unable to register", error });
  useOnAPISuccess<"authRegister">({
    state,
    data,
    onSuccess: () => {
      navigate(LOGGED_IN_HOME_PAGE);
    },
  });

  const onRegister = useCallback(() => {
    // Check first name length
    if (fName.length < MIN_NAME_LENGTH || fName.length > MAX_NAME_LENGTH) {
      toast({
        position: "top",
        status: "error",
        title: "Invalid first name length",
        description: `First name length must be between ${MIN_NAME_LENGTH} and ${MAX_NAME_LENGTH} characters long`,
      });
      return;
    }

    // Check last name length
    if (lName.length < MIN_NAME_LENGTH || fName.length > MAX_NAME_LENGTH) {
      toast({
        position: "top",
        status: "error",
        title: "Invalid last name length",
        description: `Last name length must be between ${MIN_NAME_LENGTH} and ${MAX_NAME_LENGTH} characters long`,
      });
      return;
    }

    // Check email length
    if (email.length === 0) {
      toast({
        position: "top",
        status: "error",
        title: "Invalid email",
        description: "Email length cannot be empty",
      });
      return;
    }

    // Check password length
    if (
      password.length < MIN_PASSWORD_LENGTH ||
      password.length > MAX_PASSWORD_LENGTH
    ) {
      toast({
        position: "top",
        status: "error",
        title: "Invalid password length",
        description: `Password length must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters long`,
      });
      return;
    }

    if (!appCode) {
      return;
    }

    query({ email, fName, lName, password, appCode });
  }, [appCode, email, fName, lName, password, query, toast]);

  return (
    <Box
      top={0}
      left={0}
      position={"fixed"}
      className="fullHeight"
      width={"100%"}
      display={"flex"}
      flexDir={"column"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Box
        padding={4}
        maxWidth={"400px"}
        width={"100%"}
        display={"flex"}
        flexDir={"column"}
        alignItems={"center"}
      >
        <Logo marginBottom={8} />
        <Input
          placeholder="First Name"
          marginBottom={4}
          value={fName}
          onChange={(e) => setFName(e.target.value)}
        />
        <Input
          placeholder="Last Name"
          marginBottom={4}
          value={lName}
          onChange={(e) => setLName(e.target.value)}
        />
        <Input
          placeholder="email"
          marginBottom={4}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          placeholder="password"
          type={"password"}
          marginBottom={4}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          isLoading={state === "loading"}
          isDisabled={!appCode}
          colorScheme={THEME_COLOR_SCHEME}
          onClick={onRegister}
          width={"100%"}
          size={"lg"}
        >
          Register
        </Button>
        {!appCode ? (
          <Alert status="error" marginTop={4}>
            <AlertIcon />
            App Code not detected
          </Alert>
        ) : null}
      </Box>
    </Box>
  );
};
